
import { MenuOpenChangeDetails } from "@chakra-ui/react";
import { useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { toaster } from "@/components/ui/toaster";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import type { AxiosError } from "axios";

import type { BindAccountResponse } from "../components/ApproveModal";
import type {
  DocDecision,
  LegalOnboardingDomicilio,
  LegalOnboardingDatosCvu,
  LegalOnboardingDetail,
} from "../schema/handler-functions";
import {
  bindAccountLegalOnboardingHandler,
  confirmApproveLegalOnboardingHandler,
} from "../schema/handler-functions";

export const useApproveFlow = ({
  id,
  detail,
  decisions: _decisions,
  navigate,
  fetchPermissions,
  routeState,
  onBack,
  showApproveSuccessToast,
  showApproveErrorToast,
}: {
  id: string | undefined;
  detail: LegalOnboardingDetail | null;
  decisions: Record<number, DocDecision>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  routeState: any;
  onBack: () => void;
  showApproveSuccessToast: () => void;
  showApproveErrorToast: () => void;
}) => {
  const [openDomicilio, setOpenDomicilio] = useState<boolean>(false);
  const [domicilio, setDomicilio] = useState<LegalOnboardingDomicilio | null>(
    null,
  );
  const [openApprove, setOpenApprove] = useState<boolean>(false);
  const [loadingBind, setLoadingBind] = useState<boolean>(false);
  const [bindError, setBindError] = useState<boolean>(false);
  const [bindData, setBindData] = useState<BindAccountResponse | null>(null);
  const [loadingConfirmApprove, setLoadingConfirmApprove] =
    useState<boolean>(false);
  const [retryUsed, setRetryUsed] = useState<boolean>(false);
  const [backToDomicilioPending, setBackToDomicilioPending] =
    useState<boolean>(false);

  const handleBindRequest = async (opts?: {
    isRetry?: boolean;
    domicilio?: LegalOnboardingDomicilio;
  }) => {
    try {
      setLoadingBind(true);
      setBindError(false);
      setBindData(null);

      const domicilioToUse = opts?.domicilio ?? domicilio;
      if (!domicilioToUse) throw new Error("Domicilio no disponible");

      const resp = await bindAccountLegalOnboardingHandler({
        detail,
        domicilio: domicilioToUse,
      });
      setBindData(resp);
    } catch (error) {
      setBindError(true);
      if (opts?.isRetry) setRetryUsed(true);
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "Error al obtener los nuevos datos del CVU.",
        ),
      });
      throw error;
    } finally {
      setLoadingBind(false);
    }
  };

  const onApprove = async () => {
    if (!id) return;
    const numericId = Number(id);
    if (!numericId) return;

    setRetryUsed(false);
    setBindError(false);
    setBindData(null);
    setDomicilio(null);
    setOpenApprove(false);
    setOpenDomicilio(true);
  };

  const onOpenChangeDomicilio = (e: MenuOpenChangeDetails) => {
    setOpenDomicilio(e.open);
  };

  const onCancelDomicilio = () => {
    setOpenDomicilio(false);
  };

  const onConfirmDomicilio = async (d: LegalOnboardingDomicilio) => {
    setDomicilio(d);
    setOpenDomicilio(false);
    setOpenApprove(true);
    setRetryUsed(false);
    await handleBindRequest({ domicilio: d });
  };

  const onOpenChangeApprove = (e: MenuOpenChangeDetails) => {
    if (!e.open) {
      setBindError(false);
      setBindData(null);
      setLoadingBind(false);
      setLoadingConfirmApprove(false);
      setRetryUsed(false);
      if (!backToDomicilioPending) setDomicilio(null);
      setBackToDomicilioPending(false);
    }
    setOpenApprove(e.open);
  };

  const onCancelApprove = () => {
    setBindError(false);
    setBindData(null);
    setLoadingBind(false);
    setLoadingConfirmApprove(false);
    setRetryUsed(false);
    setDomicilio(null);
    setBackToDomicilioPending(false);
    setOpenApprove(false);
  };

  const onBackToDomicilio = () => {
    setBackToDomicilioPending(true);
    setBindError(false);
    setBindData(null);
    setLoadingBind(false);
    setLoadingConfirmApprove(false);
    setRetryUsed(false);
    setOpenApprove(false);
    setOpenDomicilio(true);
  };

  const onRetryBind = async () => {
    if (!id) return;
    const numericId = Number(id);
    if (!numericId) return;
    if (retryUsed) return;
    await handleBindRequest({ isRetry: true });
  };

  const onConfirmApprove = async () => {
    if (!id) return;
    const numericId = Number(id);
    if (!numericId) return;
    if (
      !bindData?.cvu?.trim()?.length ||
      !bindData?.cvuAlias?.trim()?.length ||
      !bindData?.cuitCuil?.trim()?.length
    )
      return;
    if (!detail) return;

    const datosCvu: LegalOnboardingDatosCvu = {
      cuit: bindData.cuitCuil ?? detail.cuit,
      alias: bindData.cvuAlias,
      cvu: bindData.cvu,
      idCuenta: bindData.accountId,
      idCvu: bindData.cvuId,
    };

    try {
      setLoadingConfirmApprove(true);
      await confirmApproveLegalOnboardingHandler({
        id: numericId,
        documentos: undefined,
        decisions: undefined,
        datosCvu,
        navigate,
        fetchPermissions,
        routeState,
      });
      onCancelApprove();
      showApproveSuccessToast();
      onBack();
    } catch {
      showApproveErrorToast();
    } finally {
      setLoadingConfirmApprove(false);
    }
  };

  return {
    openDomicilio,
    domicilio,
    onOpenChangeDomicilio,
    onCancelDomicilio,
    onConfirmDomicilio,
    openApprove,
    loadingBind,
    loadingConfirmApprove,
    bindError,
    bindData,
    canRetry: bindError && !retryUsed,
    onApprove,
    onOpenChangeApprove,
    onRetryBind,
    onCancelApprove,
    onBackToDomicilio,
    onConfirmApprove,
  };
};
